import { Reference } from '@approvalmax/types';
import { DropdownEditor, Field, GlobalHotKeys, Popup } from '@approvalmax/ui';
import { Progress } from '@approvalmax/ui/src/components';
import { arrayHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain, Entities, schemas, State } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useShallowEqualSelector } from 'modules/react-redux';
import * as search from 'modules/search';
import { normalize } from 'normalizr';
import { memo, useCallback, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { useLazyGetSearchRequests } from 'shared/data/webApp/v1';
import { getPath, Path } from 'urlBuilder';

import {
    loadPageData,
    loadSearchResultsDataFailure,
    loadSearchResultsDataResponse,
    mapSearchRequestsParams,
} from '../../actions';
import { SEARCH_REQUESTS_LIST_PAGE_ID } from '../../config';
import { createEmptySearchParams } from '../../data/SearchParameters';
import { RequestSearchPopupData } from '../../reducers/page/activePopup/requestSearchPopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import {
    AmountSearchFields,
    DearSearchFields,
    NetSuiteLinesSearchFields,
    NetSuiteSearchFields,
    QBOSearchFields,
    StandaloneSearchFields,
    XeroSearchFields,
} from './components';
import { requestStatuses } from './RequestSearchPopup.constants';
import { messages } from './RequestSearchPopup.messages';
import {
    CompanyField,
    Content,
    Header,
    ImportantNotice,
    ImportantNoticeText,
    ImportantNoticeWarnIcon,
    IntegrationWarnBlock,
    Root,
    WrapFieldRow,
} from './RequestSearchPopup.styles';
import { UpdateParameterFn } from './RequestSearchPopup.types';
import { getParamsAfterTemplateChange } from './RequestSearchPopup.utils';

const qa = bemFactory.qa('reql-request-search-popup');

const RequestSearchPopup = memo(() => {
    const dispatch = useDispatch();

    const [getSearchRequests, { isFetching }] = useLazyGetSearchRequests();

    const { companies, companiesContexts, defaultSearchParams } = useShallowEqualSelector((state: State) => {
        const data = getActivePopup<RequestSearchPopupData>(state);

        const companiesWithUpdatedNames = search.selectors.getSearchContext(state)!.companies.map((company) => {
            return company.text
                ? company
                : {
                      ...company,
                      text: messages.unnamedOrganisation,
                  };
        });

        return {
            companiesContexts: companiesWithUpdatedNames,
            companies: selectors.company.getCompanies(state),
            defaultSearchParams: data.defaultSearchParams,
        };
    });
    const [params, setParams] = useState(() => {
        const company = companiesContexts[0] || null;

        return (
            defaultSearchParams || {
                ...createEmptySearchParams(),
                company,
                template: company && company.templates[0] ? company.templates[0] : null,
            }
        );
    });

    const loadSearchResults = useCallback(async () => {
        const body = mapSearchRequestsParams({
            pageIndex: 0,
            searchParams: params,
        });

        getSearchRequests({ params: { query: body } })
            .then((response) => {
                dispatch(
                    loadPageData({
                        filter: null,
                        companyId: null,
                        templateId: null,
                        requestId: null,
                        requests: [],
                        hasMore: false,
                        entities: {},
                        searchParameters: params,
                        innerPageId: SEARCH_REQUESTS_LIST_PAGE_ID,
                    })
                );

                const entities = normalize(response.Requests, [schemas.requestSchema]).entities as any as Entities;

                dispatch(
                    loadSearchResultsDataResponse({
                        entities,
                        requests: response.Requests.map((r) => r.RequestId),
                        hasMore: response.HasMore,
                    })
                );

                if (response.Requests.length === 0) {
                    notificationService.showInfoToast(messages.emptySearchNotificationText);
                }

                const firstRequest = response.Requests[0];

                routingService.push(
                    getPath(Path.searchRequestsWithCompany, firstRequest.CompanyId, {
                        requestId: firstRequest.RequestId,
                    })
                );
            })
            .catch(() => {
                dispatch(loadSearchResultsDataFailure());
            });
    }, [dispatch, getSearchRequests, params]);

    const onDone = useCallback(() => {
        if (!params.company || !params.template) {
            return;
        }

        loadSearchResults();
    }, [params, loadSearchResults]);

    const updateParameter: UpdateParameterFn = (paramName) => {
        return (value) => {
            setParams({
                ...params,
                [paramName]: value,
            });
        };
    };

    const onTemplateChange = useCallback(
        (template: search.TemplateReference | null) => {
            setParams({
                ...params,
                template,
                ...getParamsAfterTemplateChange(template),
            });
        },
        [params]
    );

    const onCompanyChange = useCallback(
        (company: search.CompanySearchContext | null) => {
            const oldParams = params;

            // Update selected template, preserve selected template if possible
            let newTemplate;

            if (!company) {
                newTemplate = null;
            } else if (
                oldParams.template &&
                oldParams.template.integrationCode &&
                company.templates.some((t) => t.integrationCode === oldParams.template!.integrationCode)
            ) {
                newTemplate = company.templates.find((t) => t.integrationCode === oldParams.template!.integrationCode)!;
            } else {
                newTemplate = company.templates[0] || null;
            }

            onTemplateChange(newTemplate);
            setParams({
                ...params,
                company,
                template: newTemplate,
                ...getParamsAfterTemplateChange(newTemplate),
            });
        },
        [params, onTemplateChange]
    );

    const integrationCode = params?.template?.integrationCode || null;
    const integrationType = params.template && domain.getIntegrationTypeByCode(integrationCode);

    const company = params.company ? companies.find((c) => params.company?.id === c.id) : undefined;
    const integrationEnabled = Boolean(company && company.flags.hasActiveIntegration);

    return (
        <GlobalHotKeys handlers={{ enter: () => onDone() }}>
            <Root
                qa={qa()}
                title={messages.popupTitle}
                buttons={
                    <Popup.DefaultContent.Button
                        qa={qa('done-button')}
                        execute={onDone}
                        title={messages.doneButtonTitle}
                        disabled={!params.company || !params.template || isFetching}
                    >
                        {messages.doneButtonText}
                    </Popup.DefaultContent.Button>
                }
            >
                <Content>
                    {isFetching && <Progress size='xsmall' />}

                    <Header>{messages.requiredBlockHeader}</Header>

                    <WrapFieldRow>
                        <CompanyField title={messages.companyFieldTitle} required>
                            <PlainDataProvider items={companiesContexts} filterAttribute='text'>
                                <DropdownEditor
                                    displayAttribute='text'
                                    placeholder={messages.companyPlaceholder}
                                    value={params.company}
                                    onChange={onCompanyChange}
                                    disabled={isFetching}
                                />
                            </PlainDataProvider>
                        </CompanyField>

                        <Field title={messages.templateFieldTitle} required>
                            <PlainDataProvider
                                items={params.company ? params.company.templates : arrayHelpers.emptyArray()}
                            >
                                <DropdownEditor
                                    buttonComponent={DropdownEditor.NoFilterButton}
                                    placeholder={messages.templatePlaceholder}
                                    value={params.template}
                                    onChange={onTemplateChange}
                                    disabled={!params.template || isFetching}
                                />
                            </PlainDataProvider>
                        </Field>

                        <Field title={messages.requestStatusFieldTitle}>
                            <PlainDataProvider items={requestStatuses}>
                                <DropdownEditor
                                    placeholder={messages.requestStatusPlaceholder}
                                    value={requestStatuses.find((s) => s.id === params.requestStatusV2) || null}
                                    onChange={(value: Reference) =>
                                        updateParameter('requestStatusV2')(
                                            value ? (value.id as typeof params.requestStatusV2) : null
                                        )
                                    }
                                    disabled={!params.template || isFetching}
                                />
                            </PlainDataProvider>
                        </Field>
                    </WrapFieldRow>

                    {!params.template && (
                        <IntegrationWarnBlock>
                            <ImportantNotice>
                                <ImportantNoticeWarnIcon width={21} height={22} />

                                <ImportantNoticeText>{messages.noActiveWorkflowWarningText}</ImportantNoticeText>
                            </ImportantNotice>
                        </IntegrationWarnBlock>
                    )}

                    {integrationType && integrationType !== domain.IntegrationType.None && !integrationEnabled && (
                        <IntegrationWarnBlock>
                            <ImportantNotice>
                                <ImportantNoticeWarnIcon width={21} height={22} />

                                <ImportantNoticeText>
                                    {messages.disconnectionWarningText({
                                        integrationName: selectors.integration.getIntegrationTypeName(integrationType),
                                    })}
                                </ImportantNoticeText>
                            </ImportantNotice>
                        </IntegrationWarnBlock>
                    )}

                    {params.company && params.template && (
                        <>
                            <Header>{messages.detailsBlockHeader}</Header>

                            {integrationType === domain.IntegrationType.None && (
                                <StandaloneSearchFields
                                    company={company}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}

                            {integrationType === domain.IntegrationType.Xero && (
                                <XeroSearchFields
                                    company={company}
                                    integrationCode={integrationCode}
                                    integrationEnabled={integrationEnabled}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}

                            {integrationType === domain.IntegrationType.QBooks && (
                                <QBOSearchFields
                                    company={company}
                                    integrationCode={integrationCode}
                                    integrationEnabled={integrationEnabled}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}

                            {integrationType === domain.IntegrationType.NetSuite && (
                                <NetSuiteSearchFields
                                    company={company}
                                    integrationCode={integrationCode}
                                    integrationEnabled={integrationEnabled}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}

                            {integrationType === domain.IntegrationType.Dear && (
                                <DearSearchFields
                                    company={company}
                                    integrationCode={integrationCode}
                                    integrationEnabled={integrationEnabled}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}

                            {company && integrationCode !== domain.IntegrationCode.NetSuiteVendor && (
                                <AmountSearchFields
                                    company={company}
                                    integrationCode={integrationCode}
                                    integrationEnabled={integrationEnabled}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}

                            {company && integrationType === domain.IntegrationType.NetSuite && (
                                <NetSuiteLinesSearchFields
                                    company={company}
                                    integrationCode={integrationCode}
                                    integrationEnabled={integrationEnabled}
                                    params={params}
                                    updateParameter={updateParameter}
                                    disabled={isFetching}
                                />
                            )}
                        </>
                    )}
                </Content>
            </Root>
        </GlobalHotKeys>
    );
});

RequestSearchPopup.displayName = 'RequestSearchPopup';

export default RequestSearchPopup;
